import React, { useState, useContext, useEffect, useRef } from 'react';
import { Page, Icon, Spinner, ButtonSelect, FormGroup, FormControl, Input, Dropdown, Button, Alert } from 'components';
import classNames from 'lib/classNames';
import ShopContext from 'context/ShopContext';
import UserContext from 'context/UserContext';
import { Scrollbars } from 'react-custom-scrollbars';
import { API_GATEWAY } from 'aws-exports';
import styles from 'styles/page.module.scss';
import { API } from 'aws-amplify';
import { useTransition } from 'react-spring';
import removeTypeName from 'lib/removeTypeName';
import useRefState from 'hooks/useRefState';

const defaultCartLinks = [{ selector: 'a[href="/cart"]', action: 'TOGGLE' }];
const actions = [
	{ value: 'TOGGLE', label: 'Toggle Cart' },
	{ value: 'OPEN', label: 'Open Cart' },
	{ value: 'CLOSE', label: 'Close Cart' },
];

const ManualSetup = () => {
	const [active, setActive] = useState('desktop');
	const shopContext = useContext(ShopContext);
	const user = useContext(UserContext);
	const { state } = shopContext;
	const [iframeSrc, setIframeSrc] = useState(
		`${API_GATEWAY}/storeFront?shop=${user.attributes.website}&manual_setup=true&origin=${window.location.origin}`
	);
	// const [tipsOpened, setTipsOpened] = useState(false);
	// const [tipsStartingPosition, setTipsStartingPosition] = useState(0);
	const iframe = useRef(null);
	const selectorOverlay = useRef(null);
	// const tipsContentRef = useRef(null);
	const [shopCustomLinks, setShopCustomLinks] = useState(removeTypeName(state.custom_links || []));
	const [customCartLinks, setCustomCartLinks, cclRef] = useRefState(shopCustomLinks);
	const discard = () => {
		setCustomCartLinks(shopCustomLinks);
		sendMessage({
			action: 'RESET_LINKS',
			selectors: shopCustomLinks.map(({ selector }) => selector),
		});
	};
	const [loading, setLoading] = useState(true);
	const [saveLoading, setSaveLoading] = useState(false);
	// const variantsPickerTransitions = useTransition(!!variantsPickerShow, null, {
	// 	from: { width: 'translateY(100%)', background: 'rgba(0,0,0,0)' },
	// 	enter: { transform: 'translateY(0%)', background: 'rgba(0,0,0,0.2)' },
	// 	leave: { transform: 'translateY(100%)', background: 'rgba(0,0,0,0)' },
	// });

	// useEffect(() => {
	// 	setLoading(true);
	// 	setTimeout(() => setLoading(false), 300);
	// }, [active]);
	const save = async () => {
		setSaveLoading(true);
		await API.post('API_GATEWAY', '/cart/links', {
			body: { custom_links: cclRef.current },
		});
		setShopCustomLinks(cclRef.current);
		setSaveLoading(false);
	};
	const transitions = useTransition(JSON.stringify(shopCustomLinks) !== JSON.stringify(customCartLinks), null, {
		from: { position: 'absolute', opacity: 0 },
		enter: { opacity: 1 },
		leave: { opacity: 0 },
		config: {
			duration: 300,
		},
	});

	const getLink = (selector) => cclRef.current.find(({ selector: s }) => s === selector);
	const removeLink = (selector) => {
		setCustomCartLinks(cclRef.current.filter(({ selector: s }) => s !== selector));
		sendMessage({
			action: 'REMOVE_LINK',
			selector,
		});
	};
	const updateLink = (selector, newLink) =>
		setCustomCartLinks(
			cclRef.current.reduce((acc, cur) => (cur.selector === selector ? [...acc, newLink] : [...acc, cur]), [])
		);

	const sendMessage = (data) => {
		if (iframe.current) iframe.current.contentWindow.postMessage(data, API_GATEWAY);
	};
	const [elementPickerEnabled, setElementPickerEnabled] = useState(false);
	const elementPicker = (status) => {
		sendMessage({
			action: 'SET_ELEMENT_PICKER',
			elementPickerEnabled: status,
		});
		setElementPickerEnabled(status);
	};

	const handleMessage = (event) => {
		const { action, selector, redirect_url } = event.data;
		const link = getLink(selector);
		switch (action) {
			case 'ADD_CART_LINK':
				if (!link) {
					setCustomCartLinks(
						[...cclRef.current].concat([
							{
								selector,
								action: 'TOGGLE',
							},
						])
					);
					elementPicker(false);
				}
				break;
			case 'GET_ACTION':
				if (link)
					sendMessage({
						action: link.action,
					});
				break;
			case 'GET_DEFAULT_ACTION':
				sendMessage({
					action: defaultCartLinks[0].action,
				});
				break;
			case 'CHANGE_SRC':
				setIframeSrc(redirect_url);
				break;
			case 'GET_SELECTED_LINKS':
				console.log(cclRef.current.map(({ selector }) => selector));
				sendMessage({
					action: 'SET_SELECTED_LINKS',
					selectors: cclRef.current.map(({ selector }) => selector),
				});
				break;
		}
	};

	useEffect(() => {
		window.addEventListener('message', handleMessage);
		return () => {
			window.removeEventListener('message', handleMessage);
		};
	}, []);

	// useEffect(() => {
	// 	if (tipsContentRef.current) setTipsStartingPosition(tipsContentRef.current.offsetHeight);
	// }, [tipsContentRef.current]);

	return (
		<Page
			background="white"
			padding="p-0"
			header={{ title: 'Manual Setup' }}
			TopContent={{
				transitions,
				render: () => (
					<Alert
						type="save_settings"
						title="unsaved changes"
						borderPosition="none"
						paddingX="px-6"
						actions={[
							{
								text: 'Discard',
								type: 'secondary',
								loading: false,
								onClick: () => discard(),
							},
							{
								text: 'Save',
								type: 'primary',
								loading: saveLoading,
								onClick: () => save(),
							},
						]}
					/>
				),
			}}
		>
			<div className={classNames('flex w-full h-full')}>
				<div
					className={classNames('flex flex-col border-r max-h-full relative overflow-hidden')}
					style={{ width: 290 }}
				>
					<Scrollbars className={classNames('flex-1')}>
						<div className={classNames('p-3')}>
							<ButtonSelect
								options={[
									{ value: 'desktop', icon: 'desktop', label: 'Desktop' },
									{ value: 'mobile-alt', icon: 'mobile-alt', label: 'Mobile' },
								]}
								value={active}
								showSeparator={false}
								inlineIcon={true}
								onChange={(value) => setActive(value)}
								noIconBG
								className={classNames('mb-4')}
							/>
							<FormGroup
								label="Cart triggers"
								info="We use buttons/links to trigger your cart to open. By default, we use all links that lead to the cart page. (You can add more links below.)"
								minWidth={0}
								noBorderBottom
							>
								{defaultCartLinks.concat(customCartLinks).map((link, key) => (
									<CartLink {...{ key, link, updateLink, removeLink }} />
								))}
							</FormGroup>
							<button
								className={classNames(
									'w-full cursor-pointer focus:outline-none rounded p-2 text-center text-white',
									elementPickerEnabled ? 'bg-red-500' : 'bg-blue-500',
									{ 'opacity-50 cursor-not-allowed': loading }
								)}
								onClick={() => elementPicker(!elementPickerEnabled)}
								disabled={loading}
							>
								{elementPickerEnabled ? 'Cancel' : 'Add New'}
							</button>
							<Alert className="mt-3">
								<p>
									If you are still having trouble setting up cart trigger please contact our support
									at{' '}
									<a href="mailto:support@monster.shop" target="_blank">
										<b>support@monster.shop</b>
									</a>
								</p>
							</Alert>
						</div>
					</Scrollbars>
					{/* <div
						className={classNames('absolute left-0 w-full bg-white')}
						style={{
							bottom: tipsOpened ? 0 : tipsStartingPosition ? `-${tipsStartingPosition}px` : '-100%',
							transition: 'bottom 0.2s ease-out',
						}}
					>
						<div
							className={classNames(
								'flex items-center px-6 py-4 shadow-sm text-gray-600 text-sm font-bold cursor-pointer'
							)}
							style={{ height: 50 }}
							onClick={() => setTipsOpened(!tipsOpened)}
						>
							<span className={classNames('flex-1 mr-2')}>Tips</span>
							<Icon icon={tipsOpened ? 'caret-down' : 'caret-up'} />
						</div>
						<div ref={tipsContentRef} className={classNames('px-6 py-4 text-gray-500 text-sm')}>
							<p className={classNames('my-2')}>Ctrl + Click on a link will follow the link</p>
						</div>
					</div> */}
				</div>

				<div className={classNames('flex-1 h-full')}>
					<div className={classNames('w-full h-full relative')}>
						{loading && <Spinner text="loading storefront" />}
						<iframe
							ref={iframe}
							onLoad={() => {
								setLoading(false);
							}}
							src={iframeSrc}
							className={classNames('h-full mx-auto', loading && 'hidden')}
							style={{
								width: active === 'desktop' ? '100%' : 350,
							}}
							onMouseLeave={() => {
								if (elementPickerEnabled && selectorOverlay.current)
									selectorOverlay.current.style.display = 'flex';
							}}
						/>
						{elementPickerEnabled && (
							<div
								ref={selectorOverlay}
								className={classNames(
									'absolute inset-0 z-20 top-0 w-full h-full text-white text-2xl flex-center'
								)}
								style={{ background: 'rgba(0,0,0,0.6)' }}
								onMouseEnter={({ target }) => (target.style.display = 'none')}
							>
								Click on the element to select it.
							</div>
						)}
					</div>
				</div>
			</div>
		</Page>
	);
};

export default ManualSetup;

//'ritzystore2.myshopify.com' ||

const CartLink = ({ link, updateLink, removeLink }) => {
	const isDefault = defaultCartLinks.includes(link);
	return (
		<div className={classNames('p-3 rounded bg-white mb-2 shadow-sm')}>
			<FormControl label={`${isDefault ? 'default' : 'additional'} trigger`} marginBottom="mb-3">
				<Input type="text" value={link.selector} onChange={(v) => console.log(v)} disabled={isDefault} />
			</FormControl>
			{/* <FormControl label="action" marginBottom="mb-3">
				<Dropdown options={actions} value={link.action} disabled={true} onChange={(v) => console.log(v)} />
			</FormControl> */}
			{!isDefault && (
				<div className={classNames('flex flex-row-reverse')}>
					<button
						className={classNames('cursor-pointer focus:outline-none text-center text-red-500 text-sm')}
						onClick={() => removeLink(link.selector)}
					>
						Remove
					</button>
				</div>
			)}
		</div>
	);
};
